import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    LinearProgress,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { Badge, CommunityGamificationService, CommunityStoriesService, Story, TourService, TourOverview, UserStats } from '../../../../services/restserver-openapi';
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import { putUserAsync } from '../../../../store/navBar/userSlice';
import { getCommunityProfile } from '../../../../utils/User/user-openapi-hooks';
import UserLevelStats from '../../Gamification/LevelAndRank/UserLevelStats';
import RankAndLevelExplanationDialog from '../../Gamification/LevelAndRank/RankAndLevelExplanationDialog';

type Props = {
    communityName: string;
    isOwner: boolean;
};

export default function AchievementsSection({ communityName, isOwner }: Props): React.ReactElement {
    const dispatch = useAppDispatch();
    const loggedInUser = useAppSelector((state) => state.user.user);
    const userRole = useAppSelector((state) => state.login.userRoleLogin);
    const userSpecificationId = useAppSelector((state) => state.login.userSpecificationId);
    const vanliferId = useAppSelector((state) => state.user.vanliferId);
    const isVerifiedHost = useAppSelector((state) => state.user.isVerifiedHost);
    const isVerifiedAgent = useAppSelector((state) => state.user.isVerifiedAgent);

    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState<UserStats | null>(null);
    const [badges, setBadges] = useState<Array<Badge>>([]);
    const [error, setError] = useState<string | null>(null);
    const [sharedToursCount, setSharedToursCount] = useState(0);
    const [sharedStoriesCount, setSharedStoriesCount] = useState(0);
    const [completedNightsCount, setCompletedNightsCount] = useState(0);
    const [aboutMeText, setAboutMeText] = useState('');
    const [aboutMeImageDataUrl, setAboutMeImageDataUrl] = useState<string | null>(null);
    const [editingAboutMe, setEditingAboutMe] = useState(false);
    const [savingAboutMe, setSavingAboutMe] = useState(false);
    const [aboutMeError, setAboutMeError] = useState<string | null>(null);

    useEffect(() => {
        let isCancelled = false;

        const loadProfile = async () => {
            if (isOwner) {
                setAboutMeText(loggedInUser.profileBio ?? '');
                setAboutMeImageDataUrl(loggedInUser.profilePictureDataUrl ?? null);
                return;
            }

            try {
                const profile = await getCommunityProfile(communityName);
                if (isCancelled) {
                    return;
                }

                setAboutMeText(profile.profileBio ?? '');
                setAboutMeImageDataUrl(profile.profilePictureDataUrl ?? null);
            } catch {
                if (!isCancelled) {
                    setAboutMeText('');
                    setAboutMeImageDataUrl(null);
                }
            }
        };

        loadProfile();

        return () => {
            isCancelled = true;
        };
    }, [communityName, isOwner, loggedInUser.profileBio, loggedInUser.profilePictureDataUrl]);

    useEffect(() => {
        let isCancelled = false;

        const load = async () => {
            if (!communityName) {
                setError('Keine Community-Daten verfügbar.');
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const [statsResponse, badgesResponse, publicToursResponse, ownToursResponse] = await Promise.all([
                    CommunityGamificationService.getUserLevelStats(communityName),
                    CommunityGamificationService.getBadges(communityName),
                    TourService.getPublicToursForVanlifer(communityName),
                    isOwner && vanliferId ? TourService.getTours(undefined, vanliferId) : Promise.resolve([] as TourOverview[]),
                ]);

                if (isCancelled) {
                    return;
                }

                setStats(statsResponse ?? null);
                setBadges(badgesResponse ?? []);

                const sharedTours = publicToursResponse ?? [];
                setSharedToursCount(sharedTours.length);

                const toursForStats = isOwner ? ownToursResponse ?? [] : sharedTours;

                const completedNights = toursForStats
                    .filter((tour) => tour.tourCompleted === true)
                    .flatMap((tour) => tour.stays ?? [])
                    .reduce((sum, stay) => {
                        if (stay.fromDate == null || stay.toDate == null) {
                            return sum;
                        }

                        const milliseconds = stay.toDate - stay.fromDate;
                        const nights = Math.max(0, Math.round(milliseconds / (24 * 60 * 60 * 1000)));
                        return sum + nights;
                    }, 0);
                setCompletedNightsCount(completedNights);

                const storiesCount = await Promise.all(
                    toursForStats
                        .map((tour) => tour.tourId)
                        .filter((tourId): tourId is number => typeof tourId === 'number')
                        .map(async (tourId) => {
                            let total = 0;
                            let cursorId: number | undefined;
                            let cursorDate: number | undefined;

                            while (true) {
                                const response = await CommunityStoriesService.getStoriesByTourId(
                                    tourId,
                                    100,
                                    cursorId,
                                    cursorDate,
                                    'ASC'
                                );

                                total += (response.stories ?? []).filter((story) => story.sharingMode === Story.sharingMode.PUBLIC).length;

                                if (response.nextCursorId == null || response.nextCursorDate == null) {
                                    break;
                                }

                                cursorId = response.nextCursorId;
                                cursorDate = response.nextCursorDate;
                            }

                            return total;
                        })
                );

                if (isCancelled) {
                    return;
                }

                setSharedStoriesCount(storiesCount.reduce((sum, value) => sum + value, 0));
            } catch {
                if (!isCancelled) {
                    setError('Erfolge konnten nicht geladen werden.');
                }
            } finally {
                if (!isCancelled) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            isCancelled = true;
        };
    }, [communityName, isOwner, vanliferId]);

    const kpis = useMemo(
        () => [
            { label: 'Auszeichnungen', value: String(badges.length) },
            { label: 'Geteilte Touren', value: String(sharedToursCount) },
            { label: 'Nächte unterwegs', value: String(completedNightsCount) },
            { label: 'Geteilte Stories', value: String(sharedStoriesCount) },
        ],
        [badges.length, completedNightsCount, sharedStoriesCount, sharedToursCount]
    );

    const nextRank = stats?.rankStats?.nextRank ?? '-';
    const nextRankNeeded = stats?.rankStats?.nextRankTravelPointsNeeded ?? 0;
    const rankProgress = Math.max(0, Math.min(100, stats?.rankStats?.travelPointsProgressToNextRankPercentage ?? 0));

    const nextLevel = stats?.levelStats?.nextLevel ?? 0;
    const nextLevelNeeded = stats?.levelStats?.nextLevelXpNeeded ?? 0;
    const levelProgress = Math.max(0, Math.min(100, stats?.levelStats?.xpProgressToNextLevelPercentage ?? 0));

    const handleAboutMeImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        if (!file.type.startsWith('image/')) {
            setAboutMeError('Bitte ein gültiges Bild auswählen.');
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            setAboutMeError('Das Bild darf maximal 2 MB groß sein.');
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const result = typeof reader.result === 'string' ? reader.result : null;
            setAboutMeImageDataUrl(result);
            setAboutMeError(null);
        };
        reader.onerror = () => {
            setAboutMeError('Bild konnte nicht gelesen werden.');
        };
        reader.readAsDataURL(file);
    };

    const handleSaveAboutMe = async () => {
        if (!isOwner || !userRole || !loggedInUser.person?.personId || !userSpecificationId) {
            return;
        }

        setSavingAboutMe(true);
        setAboutMeError(null);

        const verified = userRole === 'HOST'
            ? isVerifiedHost
            : userRole === 'AGENT'
                ? isVerifiedAgent
                : false;

        try {
            await dispatch(
                putUserAsync({
                    inputUser: {
                        ...loggedInUser,
                        profileBio: aboutMeText.trim(),
                        profilePictureDataUrl: aboutMeImageDataUrl ?? undefined,
                    },
                    specificationUserId: userSpecificationId,
                    personId: loggedInUser.person.personId,
                    userRole,
                    verified,
                })
            ).unwrap();
            setEditingAboutMe(false);
        } catch {
            setAboutMeError('Steckbrief konnte nicht gespeichert werden.');
        } finally {
            setSavingAboutMe(false);
        }
    };

    return (
        <Box sx={{ mb: 3 }}>
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="overline" color="text.secondary">
                            Steckbrief
                        </Typography>

                        {isOwner && !editingAboutMe && (
                            <Tooltip title="Steckbrief bearbeiten">
                                <IconButton size="small" onClick={() => setEditingAboutMe(true)}>
                                    <EditRoundedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>

                    {isOwner ? (
                        <>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', md: 'center' }}>
                                <Stack spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }}>
                                    <Avatar
                                        src={aboutMeImageDataUrl ?? undefined}
                                        sx={{ width: 132, height: 132 }}
                                    />
                                    <Typography variant="body2" color="text.secondary">
                                        @{communityName}
                                    </Typography>
                                    {editingAboutMe && (
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                            <Button variant="outlined" component="label" size="small">
                                                Foto hochladen
                                                <input hidden accept="image/*" type="file" onChange={handleAboutMeImageUpload} />
                                            </Button>
                                            <Button
                                                variant="text"
                                                color="inherit"
                                                onClick={() => setAboutMeImageDataUrl(null)}
                                                size="small"
                                            >
                                                Entfernen
                                            </Button>
                                        </Stack>
                                    )}
                                </Stack>

                                <Box sx={{ flex: 1, width: '100%' }}>
                                    {editingAboutMe ? (
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={5}
                                            label="Kurzer Steckbrief"
                                            value={aboutMeText}
                                            onChange={(event) => setAboutMeText(event.target.value.slice(0, 500))}
                                            disabled={savingAboutMe}
                                            helperText={`${aboutMeText.length}/500`}
                                        />
                                    ) : (
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontFamily: 'bubblegum-sans',
                                                fontWeight: 400,
                                                lineHeight: 1.55,
                                                color: 'text.primary',
                                                minHeight: 120,
                                            }}
                                        >
                                            "{aboutMeText.trim() || 'Hier kannst du einen kurzen Steckbrief zu dir hinzufügen.'}"
                                        </Typography>
                                    )}
                                </Box>
                            </Stack>

                            {aboutMeError && (
                                <Alert severity="error" sx={{ mt: 1.5 }}>
                                    {aboutMeError}
                                </Alert>
                            )}

                            <Stack direction="row" spacing={1} sx={{ mt: 2, width: '100%', justifyContent: 'flex-end' }}>
                                {editingAboutMe && (
                                    <>
                                        <Button variant="contained" onClick={handleSaveAboutMe} disabled={savingAboutMe}>
                                            Speichern
                                        </Button>
                                        <Button
                                            variant="text"
                                            onClick={() => {
                                                setAboutMeText(loggedInUser.profileBio ?? '');
                                                setAboutMeImageDataUrl(loggedInUser.profilePictureDataUrl ?? null);
                                                setAboutMeError(null);
                                                setEditingAboutMe(false);
                                            }}
                                            disabled={savingAboutMe}
                                        >
                                            Abbrechen
                                        </Button>
                                    </>
                                )}
                            </Stack>
                        </>
                    ) : (
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', md: 'center' }}>
                            <Stack spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }}>
                                <Avatar
                                    src={aboutMeImageDataUrl ?? undefined}
                                    sx={{ width: 132, height: 132 }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    @{communityName}
                                </Typography>
                            </Stack>

                            <Typography
                                variant="h6"
                                sx={{
                                    fontFamily: 'bubblegum-sans',
                                    fontWeight: 400,
                                    lineHeight: 1.55,
                                    color: 'text.primary',
                                    minHeight: 120,
                                }}
                            >
                                "{aboutMeText.trim() || 'Dieser Vanlifer hat noch keinen Steckbrief hinterlegt.'}"
                            </Typography>
                        </Stack>
                    )}
                </CardContent>
            </Card>

            {loading ? (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Skeleton variant="rounded" height={132} />
                    </Grid>
                    {[0, 1, 2, 3].map((item) => (
                        <Grid item xs={12} sm={6} md={3} key={item}>
                            <Skeleton variant="rounded" height={92} />
                        </Grid>
                    ))}
                    <Grid item xs={12}>
                        <Skeleton variant="rounded" height={132} />
                    </Grid>
                </Grid>
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : (
                <Grid container spacing={2}>
                    {kpis.map((kpi) => (
                        <Grid item xs={12} sm={6} md={3} key={kpi.label}>
                            <Card>
                                <CardContent sx={{ minHeight: 92 }}>
                                    <Typography variant="overline" color="text.secondary">
                                        {kpi.label}
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700} lineHeight={1.2} color="text.primary">
                                        {kpi.value}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}

                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
                                    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                                        Rang
                                    </Typography>
                                    <RankAndLevelExplanationDialog />
                                </Box>
                                <UserLevelStats communityName={communityName} hideAvatar={!isOwner} />
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                                    Nächstes Ziel
                                </Typography>

                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    Noch {nextRankNeeded} Travel Points bis <strong>{nextRank}</strong>
                                </Typography>
                                <LinearProgress variant="determinate" value={rankProgress} sx={{ mb: 2, height: 8, borderRadius: 99 }} />

                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    Noch {nextLevelNeeded} Travel Points bis Level <strong>{nextLevel}</strong>
                                </Typography>
                                <LinearProgress variant="determinate" value={levelProgress} sx={{ height: 8, borderRadius: 99 }} color="secondary" />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
}
